import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Swords, Scale, ListChecks, FileCheck, ShieldAlert, CheckCircle2, XCircle, Loader2, GitBranch } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Loading, SeverityBadge, StatusBadge, RegStatusBadge, Card, SandboxTag, ReadAloud } from "../components/common";
import { toast } from "sonner";

const TABS = ["Overview", "Governance", "Regulatory", "Risk", "Data", "Security", "Red Team", "Model", "Approval", "Compliance", "Evidence", "Monitoring", "Business Value", "Audit"];

const LC_STAGES = ["Governance", "AI Policy", "Risk Assessment", "Data Governance", "Security", "Model Governance", "Human Approval", "Compliance", "Continuous Monitoring"];

export default function UseCaseDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const nav = useNavigate();
  const [uc, setUc] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [sim, setSim] = useState(null);
  const [gate, setGate] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/usecases/${id}`).then((r) => setUc(r.data));
  useEffect(() => { load(); api.get(`/usecases/${id}/security-gate`).then((r) => setGate(r.data)); }, [id]);

  if (!uc) return <Loading />;

  const runDemo = async () => {
    setBusy(true); setSim(null);
    try { const { data } = await api.post(`/usecases/${id}/simulate`); setSim(data); setTab("Overview"); toast.success(`Sandbox demo: ${data.result}`); }
    finally { setBusy(false); }
  };

  const retest = async (rt) => {
    const { data } = await api.post(`/redteam/${rt.id}/retest`);
    toast.success(`Retest ${data.result} (simulated)`);
    load(); api.get(`/usecases/${id}/security-gate`).then((r) => setGate(r.data));
  };

  const Field = ({ label, value }) => (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-800 break-words">{value || "—"}</p>
    </div>
  );

  const summary = `Use case ${uc.id}, ${uc.name_en}, in ${uc.industry}. Risk level ${uc.risk_level}. Lifecycle stage ${uc.lifecycle_stage}. Red team status ${uc.red_team_status}. Compliance ${uc.compliance_status}.`;

  return (
    <div>
      <button data-testid="back-btn" onClick={() => nav("/usecases")} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4 flip-x" /> {t("back")}</button>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 card-shadow animate-fade-up">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-[var(--qatar)]">{uc.id}</span>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">{uc.industry}</span>
            </div>
            <h1 className="mt-1 text-2xl font-bold font-head text-slate-900">{uc.name_en}</h1>
            <p dir="rtl" className="text-sm text-slate-400">{uc.name_ar}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <SeverityBadge level={uc.risk_level} testid="uc-risk-badge" />
              <StatusBadge status={uc.lifecycle_stage} />
              <StatusBadge status={uc.red_team_status} />
              <StatusBadge status={uc.compliance_status} />
              <StatusBadge status={uc.production_status} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ReadAloud text={summary} />
            <button data-testid="run-demo-btn" onClick={runDemo} disabled={busy} className="qatar-gradient inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {t("run_demo")}
            </button>
          </div>
        </div>

        {/* Security gate banner */}
        {gate && (
          <div data-testid="security-gate-banner" className={`mt-4 flex items-start gap-3 rounded-xl border p-3 text-sm ${gate.blocked ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
            {gate.blocked ? <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />}
            <div>
              <p className="font-bold">{t("security_gate")}: {gate.blocked ? t("blocked") : t("passed")}</p>
              <p className="text-xs opacity-90">{gate.reason}{gate.blocked ? ` — ${gate.findings.length} open finding(s) must be remediated & retested.` : ""}</p>
            </div>
          </div>
        )}
      </div>

      {/* Sandbox demo result */}
      {sim && (
        <Card title="Sandbox Demo Trace" className="mt-4" right={<SandboxTag />}>
          <div className="mb-3"><span className={`rounded-lg px-3 py-1 text-sm font-bold ${sim.result === "APPROVED" ? "bg-emerald-100 text-emerald-700" : sim.result === "FAILED SECURITY" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{sim.result}</span></div>
          <ol className="space-y-1.5">
            {sim.steps.map((st, i) => (
              <li key={i} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm">
                {st.status === "BLOCK" ? <XCircle className="h-4 w-4 text-red-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                <span className="font-medium text-slate-700">{st.step}</span>
                <span className="text-xs text-slate-500">{st.detail}</span>
              </li>
            ))}
          </ol>
          <p className="mt-2 text-xs text-slate-400">✓ Audit event created for this simulation.</p>
        </Card>
      )}

      {/* Tabs */}
      <div className="mt-4 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((tb) => (
          <button key={tb} data-testid={`tab-${tb.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(tb)}
            className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${tab === tb ? "border-[var(--qatar)] text-[var(--qatar)]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{tb}</button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "Overview" && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Business Function" value={uc.business_function} />
            <Field label="AI Type" value={uc.ai_type} />
            <Field label="AI Capability" value={uc.ai_capability} />
            <Field label="Business Owner" value={uc.business_owner} />
            <Field label="AI Owner" value={uc.ai_owner} />
            <Field label="Risk Owner" value={uc.risk_owner} />
            <Field label="Model" value={uc.model} />
            <Field label="Agent" value={uc.agent} />
            <Field label="Data Classification" value={uc.data_classification} />
            <Field label="Privacy Classification" value={uc.privacy_classification} />
            <Field label="Criticality" value={uc.criticality} />
            <Field label="Human Oversight" value={uc.human_oversight} />
            <div className="sm:col-span-2 lg:col-span-3"><Field label="Business Problem" value={uc.business_problem} /></div>
          </div>
        )}

        {tab === "Governance" && (
          <Card title="9-Stage Governance Lifecycle">
            <ol className="relative space-y-3">
              {(uc.lifecycle || []).map((st, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold ${st.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : st.status === "IN PROGRESS" ? "bg-[var(--qatar-subtle)] text-[var(--qatar)]" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                  <div className="flex-1"><p className="text-sm font-semibold text-slate-800">{st.stage}</p><p className="text-xs text-slate-400">Owner: {st.owner} · Due {st.due_date}</p></div>
                  <StatusBadge status={st.status} />
                </li>
              ))}
            </ol>
          </Card>
        )}

        {tab === "Regulatory" && (
          <Card title="Qatar Regulatory Mapping">
            <p className="mb-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">Mappings are advisory for governance assessment, not legal advice. Draft/voluntary items are clearly labelled.</p>
            <div className="flex flex-wrap gap-2">{(uc.regulations || []).map((r) => <span key={r} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">{r}</span>)}</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Standards" value={(uc.standards || []).join(", ")} />
              <Field label="Required Controls" value={(uc.controls || []).join(", ")} />
              <Field label="Required Evidence" value={(uc.required_evidence || []).join(", ")} />
              <Field label="Data Sources" value={(uc.data_sources || []).join(", ")} />
            </div>
          </Card>
        )}

        {tab === "Red Team" && (
          <Card title="AI Red Team Findings" right={<SandboxTag />}>
            {(uc.related_redteam || []).length === 0 ? <p className="text-sm text-slate-400">No red-team tests for this use case.</p> : (
              <div className="space-y-2">
                {uc.related_redteam.map((rt) => (
                  <div key={rt.id} className="flex flex-col gap-2 rounded-xl border border-slate-100 p-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2"><span className="font-mono text-xs font-bold text-[var(--qatar)]">{rt.id}</span><SeverityBadge level={rt.severity} /><StatusBadge status={rt.status} /></div>
                      <p className="mt-1 text-sm font-medium text-slate-800">{rt.category}</p>
                      <p className="text-xs text-slate-500">{rt.finding} · {rt.recommendation}</p>
                    </div>
                    {rt.status === "Open" && <button data-testid={`retest-${rt.id}`} onClick={() => retest(rt)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">{t("retest")}</button>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {tab === "Risk" && <RelTable rows={uc.related_risks} cols={[["id", "ID"], ["category", "Category"], ["inherent_risk", "Inherent"], ["residual_risk", "Residual"], ["risk_level", "Level", (r) => <SeverityBadge level={r.risk_level} />], ["status", "Status", (r) => <StatusBadge status={r.status} />]]} empty="No risks" />}
        {tab === "Compliance" && <RelTable rows={uc.related_compliance} cols={[["id", "ID"], ["regulation", "Regulation"], ["requirement", "Requirement"], ["status", "Status", (r) => <StatusBadge status={r.status} />], ["evidence", "Evidence"]]} empty="No compliance records" />}
        {tab === "Evidence" && <RelTable rows={uc.related_evidence} cols={[["id", "ID"], ["evidence_type", "Type"], ["description", "Description"], ["status", "Status", (r) => <StatusBadge status={r.status} />], ["reviewer", "Reviewer"]]} empty="No evidence" />}
        {tab === "Monitoring" && <RelTable rows={uc.related_monitoring} cols={[["type", "Event"], ["detail", "Detail"], ["severity", "Severity", (r) => <SeverityBadge level={r.severity} />], ["status", "Status", (r) => <StatusBadge status={r.status} />]]} empty="No monitoring events" />}
        {tab === "Approval" && <RelTable rows={uc.related_approvals} cols={[["id", "ID"], ["required_approvers", "Approvers"], ["risk_level", "Risk", (r) => <SeverityBadge level={r.risk_level} />], ["status", "Status", (r) => <StatusBadge status={r.status} />]]} empty="No approvals" />}

        {tab === "Data" && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"><Field label="Classification" value={uc.data_classification} /><Field label="Privacy" value={uc.privacy_classification} /><Field label="Data Owner" value={uc.data_owner} /><Field label="Sources" value={(uc.data_sources || []).join(", ")} /><Field label="Residency" value="Qatar" /><Field label="Human Oversight" value={uc.human_oversight} /></div>}
        {tab === "Security" && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"><Field label="Security Status" value={uc.security_status} /><Field label="Red Team Status" value={uc.red_team_status} /><Field label="Controls" value={(uc.controls || []).join(", ")} /></div>}
        {tab === "Model" && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"><Field label="Model" value={uc.model} /><Field label="Agent" value={uc.agent} /><Field label="AI Type" value={uc.ai_type} /></div>}
        {tab === "Business Value" && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"><Field label="Business Value" value={uc.business_value} /><Field label="Estimated Cost" value={`$${(uc.estimated_cost || 0).toLocaleString()}`} /><Field label="Expected Value" value={`$${(uc.expected_value || 0).toLocaleString()}`} /><Field label="Actual Value" value={`$${(uc.actual_value || 0).toLocaleString()}`} /><Field label="ROI (synthetic)" value={`${uc.roi}%`} /><Field label="Value Realisation" value={`${uc.value_realisation}%`} /></div>}
        {tab === "Audit" && <p className="text-sm text-slate-500">Audit events for this use case are recorded centrally in the Audit Center, including creation, simulations, retests and approvals.</p>}
      </div>
    </div>
  );
}

function RelTable({ rows, cols, empty }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-slate-400">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white card-shadow">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-100 bg-slate-50/60">{cols.map((c) => <th key={c[0]} className="px-4 py-2.5 text-start text-xs font-semibold uppercase tracking-wider text-slate-500">{c[1]}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-50"><>{cols.map((c) => <td key={c[0]} className="px-4 py-2.5 text-slate-700">{c[2] ? c[2](r) : r[c[0]]}</td>)}</></tr>)}</tbody>
      </table>
    </div>
  );
}
